import React, { useEffect, useState, useCallback } from "react";
import { useFirebase } from "../../../context/Firebase";
import { collection, getDocs, deleteDoc, doc, query, orderBy, updateDoc } from "firebase/firestore";
import { Link, useNavigate } from "react-router-dom";
import Sidebar from "../../../shared/components/Sidebar";
import PageLayout from "../../../shared/components/PageLayout";
import {
  Button,
  CircularProgress,
  Chip,
  Snackbar,
  ToggleButton,
  ToggleButtonGroup,
} from "@mui/material";
import "./jobs.css";
import DOMPurify from "dompurify";
import { format } from "date-fns";

const Jobs = () => {
  const { db } = useFirebase();
  const navigate = useNavigate();
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState("all");

  const fetchJobs = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const q = query(collection(db, "jobs"), orderBy("createdAt", "desc"));
      const querySnapshot = await getDocs(q);
      const jobList = querySnapshot.docs.map((d) => ({
        id: d.id,
        ...d.data(),
      }));
      setJobs(jobList);
    } catch (err) {
      console.error("Fetch jobs error:", err);
      // Fallback: fetch without ordering if index is missing
      try {
        const querySnapshot = await getDocs(collection(db, "jobs"));
        const jobList = querySnapshot.docs.map((d) => ({
          id: d.id,
          ...d.data(),
        }));
        // Sort client-side
        jobList.sort((a, b) => {
          const aTime = a.createdAt?.toDate?.() || (a.createdAt?.seconds ? new Date(a.createdAt.seconds * 1000) : new Date(0));
          const bTime = b.createdAt?.toDate?.() || (b.createdAt?.seconds ? new Date(b.createdAt.seconds * 1000) : new Date(0));
          return bTime - aTime;
        });
        setJobs(jobList);
      } catch (fallbackErr) {
        setError("Failed to fetch jobs. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  }, [db]);

  useEffect(() => {
    fetchJobs();
  }, [fetchJobs]);

  const handleDelete = async (id) => {
    try {
      await deleteDoc(doc(db, "jobs", id));
      setJobs((prevJobs) => prevJobs.filter((job) => job.id !== id));
    } catch (err) {
      setError("Failed to delete job. Please try again.");
    }
  };

  const handleToggleTrending = async (id, currentTrending) => {
    try {
      await updateDoc(doc(db, "jobs", id), { isTrending: !currentTrending });
      setJobs((prev) =>
        prev.map((job) => job.id === id ? { ...job, isTrending: !currentTrending } : job)
      );
    } catch (err) {
      setError("Failed to update trending status.");
    }
  };

  const filteredJobs = filter === "trending" ? jobs.filter((j) => j.isTrending) : jobs;

  return (
    <div className="dashboard">
      <Sidebar />
      <div className="dashboard--content job-list">
        <Snackbar open={!!error} autoHideDuration={6000} message={error} onClose={() => setError(null)} />
        <PageLayout
          title="Quick Reads"
          actions={
            <>
              <Button variant="outlined" onClick={() => navigate("/QuickReadsCategory")}>Quick Reads Category</Button>
              <Button variant="contained" onClick={() => navigate("/add-job")}>Add New Information</Button>
            </>
          }
        >
          <ToggleButtonGroup
            value={filter}
            exclusive
            onChange={(e, val) => val && setFilter(val)}
            size="small"
            sx={{ mb: 2 }}
          >
            <ToggleButton value="all">All</ToggleButton>
            <ToggleButton value="trending">Trending</ToggleButton>
          </ToggleButtonGroup>

          {loading ? (
            <div className="loader"><CircularProgress /></div>
          ) : (
            <div className="job-list__container">
              {filteredJobs.length === 0 ? (
                <p>No items available.</p>
              ) : (
                filteredJobs.map((job) => (
                  <JobCard key={job.id} job={job} onDelete={handleDelete} onToggleTrending={handleToggleTrending} />
                ))
              )}
            </div>
          )}
        </PageLayout>
      </div>
    </div>
  );
};

const JobCard = ({ job, onDelete, onToggleTrending }) => {
  const formattedDate = job.createdAt?.toDate
    ? format(job.createdAt.toDate(), "dd MMM yyyy, hh:mm a")
    : job.createdAt?.seconds
    ? format(new Date(job.createdAt.seconds * 1000), "dd MMM yyyy, hh:mm a")
    : "Just now";

  return (
    <div className="job-card" style={{ position: 'relative' }}>
      {/* Trending chip - top right, absolute */}
      <Chip
        label={job.isTrending ? "🔥 Trending" : "Not Trending"}
        color={job.isTrending ? "warning" : "default"}
        size="small"
        onClick={() => onToggleTrending(job.id, job.isTrending)}
        clickable
        sx={{ position: 'absolute', top: 12, right: 12, zIndex: 1 }}
      />

      {/* Thumbnail image at top */}
      {job.imageUrl && (
        <img
          src={job.imageUrl}
          alt={job.title}
          className="job-card-image"
        />
      )}

      <h3>{job.title}</h3>
      <small className="job-date">{formattedDate}</small>

      {/* Render HTML description */}
      <div
        className="job-description"
        dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(job.description || "") }}
      />

      <div className="actions">
        <Link to={`/jobs/${job.id}`} className="chip-wrapper">
          <Chip label="View" color="success" clickable className="chip-full" />
        </Link>
        <div className="chip-wrapper">
          <Chip
            label="Delete"
            color="error"
            onClick={() => onDelete(job.id)}
            className="chip-full"
          />
        </div>
      </div>
    </div>
  );
};

export default Jobs;
